import { Dispatch } from 'redux';
import { InAppNotification } from '../../models';
import Notifications from '../../services/Notifications';
import {
    createNotificationInboxChannel,
    nextUnreadCountAfterCreate,
    publishNotificationInboxUpdate,
} from '../../utils/notificationInboxSync';
import type { Callback, Status } from './rest';

const broadcastInboxUpdate = (update: { type: string; data: AnyObj }) => {
    const channel = createNotificationInboxChannel();
    publishNotificationInboxUpdate(channel, update);
    channel?.close();
};

const LOADING_NOTIFICATIONS              = 'yoda/notifications/LOADING_NOTIFICATIONS';
const LOADING_NOTIFICATIONS_SUCCESS      = 'yoda/notifications/LOADING_NOTIFICATIONS_SUCCESS';
const LOADING_NOTIFICATIONS_FAILURE      = 'yoda/notifications/LOADING_NOTIFICATIONS_FAILURE';
const LOADING_UNREAD_COUNT               = 'yoda/notifications/LOADING_UNREAD_COUNT';
const LOADING_UNREAD_COUNT_SUCCESS       = 'yoda/notifications/LOADING_UNREAD_COUNT_SUCCESS';
const LOADING_UNREAD_COUNT_FAILURE       = 'yoda/notifications/LOADING_UNREAD_COUNT_FAILURE';
const MARKING_NOTIFICATION_READ          = 'yoda/notifications/MARKING_NOTIFICATION_READ';
const MARKING_NOTIFICATION_READ_SUCCESS  = 'yoda/notifications/MARKING_NOTIFICATION_READ_SUCCESS';
const MARKING_NOTIFICATION_READ_FAILURE  = 'yoda/notifications/MARKING_NOTIFICATION_READ_FAILURE';
const MARKING_ALL_NOTIFICATIONS_READ     = 'yoda/notifications/MARKING_ALL_NOTIFICATIONS_READ';
const MARKING_ALL_NOTIFICATIONS_SUCCESS  = 'yoda/notifications/MARKING_ALL_NOTIFICATIONS_SUCCESS';
const MARKING_ALL_NOTIFICATIONS_FAILURE  = 'yoda/notifications/MARKING_ALL_NOTIFICATIONS_FAILURE';
const CLEARING_ALL_NOTIFICATIONS         = 'yoda/notifications/CLEARING_ALL_NOTIFICATIONS';
const CLEARING_ALL_NOTIFICATIONS_SUCCESS = 'yoda/notifications/CLEARING_ALL_NOTIFICATIONS_SUCCESS';
const CLEARING_ALL_NOTIFICATIONS_FAILURE = 'yoda/notifications/CLEARING_ALL_NOTIFICATIONS_FAILURE';
const DELETING_NOTIFICATION              = 'yoda/notifications/DELETING_NOTIFICATION';
const DELETING_NOTIFICATION_SUCCESS      = 'yoda/notifications/DELETING_NOTIFICATION_SUCCESS';
const DELETING_NOTIFICATION_FAILURE      = 'yoda/notifications/DELETING_NOTIFICATION_FAILURE';
const REMOTE_UPDATE                      = 'yoda/mercure/REMOTE_UPDATE';

interface NotificationsState {
    notifications: InAppNotification[];
    unreadCount: number;
    loading: Status;
    loadingError: string | null;
    loadingCount: Status;
    loadingCountError: string | null;
    updating: Status;
    updatingError: string | null;
}

const initialState: NotificationsState = {
    notifications: [] as InAppNotification[],
    unreadCount: 0,
    loading: 'idle',
    loadingError: null,
    loadingCount: 'idle',
    loadingCountError: null,
    updating: 'idle',
    updatingError: null,
};

const upsertNotification = (notifications: InAppNotification[], notification: InAppNotification) => {
    const next = notifications.filter(item => item._id !== notification._id);
    return [notification, ...next]
        .sort((a, b) => (b.createdAt?.valueOf() || 0) - (a.createdAt?.valueOf() || 0))
        .slice(0, 20);
};

export default function reducer(state: NotificationsState = initialState, action: any): NotificationsState {
    switch (action.type) {
        case LOADING_NOTIFICATIONS:
            return {
                ...state,
                loading: 'pending',
                loadingError: null,
            };
        case LOADING_NOTIFICATIONS_SUCCESS:
            return {
                ...state,
                loading: 'succeeded',
                notifications: action.notifications,
            };
        case LOADING_NOTIFICATIONS_FAILURE:
            return {
                ...state,
                loading: 'failed',
                loadingError: action.error.message,
            };
        case LOADING_UNREAD_COUNT:
            return {
                ...state,
                loadingCount: 'pending',
                loadingCountError: null,
            };
        case LOADING_UNREAD_COUNT_SUCCESS:
            return {
                ...state,
                loadingCount: 'succeeded',
                unreadCount: action.count,
            };
        case LOADING_UNREAD_COUNT_FAILURE:
            return {
                ...state,
                loadingCount: 'failed',
                loadingCountError: action.error.message,
            };
        case MARKING_NOTIFICATION_READ:
        case MARKING_ALL_NOTIFICATIONS_READ:
        case CLEARING_ALL_NOTIFICATIONS:
        case DELETING_NOTIFICATION:
            return {
                ...state,
                updating: 'pending',
                updatingError: null,
            };
        case MARKING_NOTIFICATION_READ_SUCCESS:
            return {
                ...state,
                updating: 'succeeded',
                notifications: state.notifications.map(notification => {
                    if (notification._id === action.notification._id)
                        return action.notification;
                    return notification;
                }),
                unreadCount: Math.max(0, state.unreadCount - (action.wasUnread ? 1 : 0)),
            };
        case MARKING_ALL_NOTIFICATIONS_SUCCESS:
            return {
                ...state,
                updating: 'succeeded',
                notifications: state.notifications.map(notification => {
                    return new InAppNotification({
                        ...notification,
                        readAt: action.readAt
                    });
                }),
                unreadCount: 0,
            };
        case CLEARING_ALL_NOTIFICATIONS_SUCCESS:
            return {
                ...state,
                updating: 'succeeded',
                notifications: [],
                unreadCount: 0,
            };
        case DELETING_NOTIFICATION_SUCCESS:
            return {
                ...state,
                updating: 'succeeded',
                notifications: state.notifications.filter(
                    notification => notification._id !== action.notificationId
                ),
                unreadCount: Math.max(0, state.unreadCount - (action.wasUnread ? 1 : 0)),
            };
        case MARKING_NOTIFICATION_READ_FAILURE:
        case MARKING_ALL_NOTIFICATIONS_FAILURE:
        case CLEARING_ALL_NOTIFICATIONS_FAILURE:
        case DELETING_NOTIFICATION_FAILURE:
            return {
                ...state,
                updating: 'failed',
                updatingError: action.error.message,
            };
        case REMOTE_UPDATE:
            if (action.update?.type === 'notification.created') {
                const notification = new InAppNotification(action.update.data);
                if (!notification.user)
                    return state;
                // Dedup: Mercure + BroadcastChannel can deliver the same event.
                const previous = state.notifications.find(item => item._id === notification._id);
                return {
                    ...state,
                    notifications: upsertNotification(state.notifications, notification),
                    unreadCount: nextUnreadCountAfterCreate(
                        state.unreadCount,
                        previous,
                        notification
                    ),
                };
            }
            if (action.update?.type === 'notification.updated') {
                const notification = new InAppNotification(action.update.data);
                if (!notification.user)
                    return state;
                const previous = state.notifications.find(item => item._id === notification._id);
                return {
                    ...state,
                    notifications: upsertNotification(state.notifications, notification),
                    unreadCount: Math.max(0, state.unreadCount - (previous && !previous.readAt && notification.readAt ? 1 : 0)),
                };
            }
            if (action.update?.type === 'notification.read_all') {
                // Require user so another actor's read-all cannot clear this inbox
                // (Mercure currently subscribes to "*").
                if (!action.update.data?.user)
                    return state;
                return {
                    ...state,
                    notifications: state.notifications.map(notification => new InAppNotification({
                        ...notification,
                        readAt: action.update.data.readAt
                    })),
                    unreadCount: 0,
                };
            }
            if (action.update?.type === 'notification.clear_all') {
                if (!action.update.data?.user)
                    return state;
                return {
                    ...state,
                    notifications: [],
                    unreadCount: 0,
                };
            }
            if (action.update?.type === 'notification.deleted') {
                const deletedId = action.update.data?._id;
                if (!deletedId || !action.update.data?.user)
                    return state;
                const previous = state.notifications.find(item => item._id === deletedId);
                const wasUnread = previous
                    ? !previous.readAt
                    : Boolean(action.update.data.wasUnread);
                return {
                    ...state,
                    notifications: state.notifications.filter(
                        notification => notification._id !== deletedId
                    ),
                    unreadCount: Math.max(0, state.unreadCount - (wasUnread ? 1 : 0)),
                };
            }
            return state;
        default:
            return state;
    }
}

export const loadNotifications = (params?: AnyObj, callback?: Callback) => {
    return (dispatch: Dispatch) => {
        dispatch({ type: LOADING_NOTIFICATIONS });
        return Notifications.list(params || { limit: 20 })
            .then((data: AnyObj) => {
                dispatch({ type: LOADING_NOTIFICATIONS_SUCCESS, notifications: data.notifications });
                callback && callback(/*err*/null, data.notifications);
            })
            .catch((err: Error) => {
                dispatch({ type: LOADING_NOTIFICATIONS_FAILURE, error: err });
                callback && callback(err);
            });
    };
}

export const loadUnreadNotificationCount = (callback?: Callback) => {
    return (dispatch: Dispatch) => {
        dispatch({ type: LOADING_UNREAD_COUNT });
        return Notifications.unreadCount()
            .then((data: AnyObj) => {
                dispatch({ type: LOADING_UNREAD_COUNT_SUCCESS, count: data.count || 0 });
                callback && callback(/*err*/null, data.count || 0);
            })
            .catch((err: Error) => {
                dispatch({ type: LOADING_UNREAD_COUNT_FAILURE, error: err });
                callback && callback(err);
            });
    };
}

export const markNotificationAsRead = (notification: InAppNotification, callback?: Callback) => {
    return (dispatch: Dispatch) => {
        const wasUnread = !notification.readAt;
        dispatch({ type: MARKING_NOTIFICATION_READ });
        return Notifications.markAsRead(notification._id)
            .then((data: AnyObj) => {
                dispatch({ type: MARKING_NOTIFICATION_READ_SUCCESS, notification: data.notification, wasUnread });
                if (data.notification)
                    broadcastInboxUpdate({ type: 'notification.updated', data: data.notification });
                callback && callback(/*err*/null, data.notification);
            })
            .catch((err: Error) => {
                dispatch({ type: MARKING_NOTIFICATION_READ_FAILURE, error: err });
                callback && callback(err);
            });
    };
}

export const markAllNotificationsAsRead = (callback?: Callback) => {
    return (dispatch: Dispatch, getState: () => AnyObj) => {
        dispatch({ type: MARKING_ALL_NOTIFICATIONS_READ });
        return Notifications.markAllAsRead()
            .then((data: AnyObj) => {
                dispatch({ type: MARKING_ALL_NOTIFICATIONS_SUCCESS, readAt: data.readAt });
                const userId = getState()?.auth?.user?._id;
                if (userId)
                    broadcastInboxUpdate({
                        type: 'notification.read_all',
                        data: { readAt: data.readAt, user: userId },
                    });
                callback && callback(/*err*/null, data);
            })
            .catch((err: Error) => {
                dispatch({ type: MARKING_ALL_NOTIFICATIONS_FAILURE, error: err });
                callback && callback(err);
            });
    };
}

export const clearAllNotifications = (callback?: Callback) => {
    return (dispatch: Dispatch, getState: () => AnyObj) => {
        dispatch({ type: CLEARING_ALL_NOTIFICATIONS });
        return Notifications.clearAll()
            .then((data: AnyObj) => {
                dispatch({ type: CLEARING_ALL_NOTIFICATIONS_SUCCESS });
                const userId = getState()?.auth?.user?._id;
                if (userId)
                    broadcastInboxUpdate({
                        type: 'notification.clear_all',
                        data: { user: userId },
                    });
                callback && callback(/*err*/null, data);
            })
            .catch((err: Error) => {
                dispatch({ type: CLEARING_ALL_NOTIFICATIONS_FAILURE, error: err });
                callback && callback(err);
            });
    };
}

export const deleteNotification = (notification: InAppNotification, callback?: Callback) => {
    return (dispatch: Dispatch) => {
        const wasUnread = !notification.readAt;
        dispatch({ type: DELETING_NOTIFICATION });
        return Notifications.deleteOne(notification._id)
            .then((data: AnyObj) => {
                dispatch({
                    type: DELETING_NOTIFICATION_SUCCESS,
                    notificationId: notification._id,
                    wasUnread,
                });
                const payload = data?.notification || {
                    _id: notification._id,
                    user: notification.user,
                    wasUnread,
                };
                broadcastInboxUpdate({ type: 'notification.deleted', data: payload });
                callback && callback(/*err*/null, data);
            })
            .catch((err: Error) => {
                dispatch({ type: DELETING_NOTIFICATION_FAILURE, error: err });
                callback && callback(err);
            });
    };
}
